import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import purchaseOrderAPI from '../../api/purchaseOrderAPI';
import { formatDate } from '../../utils/dateUtils';
import { formatNumber } from '../../utils/numberUtils';

const PurchaseOrderList = () => {
    const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
    const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const [modalOpen, setModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchPurchaseOrders();
    }, []);

    useEffect(() => {
        filterPurchaseOrders();
    }, [searchTerm, statusFilter, allPurchaseOrders]);

    const fetchPurchaseOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await purchaseOrderAPI.getAll({
                populate: 'supplier,store' // Add this if your backend supports it
            });

           // console.log('API Response:', response.data); // Add this to check the structure
    
            // Transform data if needed
            const purchaseOrders = response.data.map(po => ({
                ...po,
                // Ensure nested objects exist
                _id: po._id,
                supplier: po.supplier || { name: 'N/A' },
                store: po.store || { name: 'N/A' }
            }));

            console.log('purchaseOrders', purchaseOrders);
    
            setAllPurchaseOrders(purchaseOrders);
        } catch (error) {
            setError(error.message || 'Failed to load purchase orders');
            setAllPurchaseOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const filterPurchaseOrders = () => {
        let filtered = [...allPurchaseOrders];

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(po => po.status === statusFilter);
        }

        // Apply search term filter
        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(po => 
                (po.poNumber?.toLowerCase() || '').includes(lowercasedSearch) ||
                (po.supplier?.name?.toLowerCase() || '').includes(lowercasedSearch) ||
                (po.store?.name?.toLowerCase() || '').includes(lowercasedSearch) ||
                (formatDate(po.orderDate)?.includes(lowercasedSearch)
            ));
        }

        setFilteredPurchaseOrders(filtered);
    };

    const handleDelete = async () => {
        try {
            await purchaseOrderAPI.cancel(selectedPurchaseOrder._id);
            setAllPurchaseOrders(allPurchaseOrders.map(po => 
                po._id === selectedPurchaseOrder._id ? {...po, status: 'Cancelled'} : po
            ));
            setConfirmModalOpen(false);
        } catch (err) {
            setError(err.message || 'Failed to cancel purchase order');
        }
    };

    const openDetails = (purchaseOrder) => {
        navigate(`/purchase-order/${purchaseOrder._id}`);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
    };

    const statusBadgeClass = (status) => {
        switch (status) {
            case 'Draft':
                return 'bg-gray-100 text-gray-800';
            case 'Ordered':
                return 'bg-blue-100 text-blue-800';
            case 'Received':
                return 'bg-green-100 text-green-800';
            case 'Cancelled':
                return 'bg-red-100 text-red-800';
            case 'Returned':
                return 'bg-orange-100 text-orange-800';
            case 'Partially_Returned':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const columns = [
        {
            title: 'PO Number',
            key: 'poNumber',
            render: (po) => ( // Changed from dataIndex to render
                <span className="font-medium">{po.poNumber}</span>
            )
        },
        {
            title: 'Supplier',
            key: 'supplier',
            render: (po) => {
                // Handle both populated objects and raw IDs
                if (typeof po.supplier === 'object') {
                    return po.supplier?.name || 'N/A';
                }
                return 'N/A (ID: ' + po.supplier + ')'; // Fallback for unpopulated
            }
        },
        {
            title: 'Date',
            key: 'orderDate',
            render: (po) => formatDate(po.orderDate)
        },
        {
            title: 'Total',
            key: 'total',
            render: (po) => `${formatNumber(po.total) || '0.00'}`
        },
        {
            title: 'Status',
            key: 'status',
            render: (po) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(po.status)}`}>
                    {po.status}
                </span>
            )
        }
    ];

    return (
        <Layout pageTitle="Purchase Orders">
            <div className="bg-gray-100 mx-auto p-6">

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                        <DebouncedSearchInput
                            placeholder="Search by PO#, supplier, store or date..."
                            onSearch={handleSearch}
                            className="flex-1 mr-4 max-w-md"
                        />

                        <select
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <option value="all">All Statuses</option>
                            <option value="Draft">Draft</option>
                            <option value="Ordered">Ordered</option>
                            <option value="Received">Received</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>
                    </div>

                    <button 
                        onClick={() => navigate('/purchase-order/new')}
                        className="transition-all duration-300 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center"
                    >
                        <span className="mr-1">+</span> Create PO
                    </button>
                </div>

                <div className="mb-2">
                    {searchTerm && (
                        <div className="text-sm text-gray-500">
                            Showing {filteredPurchaseOrders.length} of {allPurchaseOrders.length} purchase orders matching "{searchTerm}"
                        </div>
                    )}
                </div>

                
                <DataTable
                    columns={columns}
                    data={filteredPurchaseOrders}
                    loading={loading}
                    emptyMessage="No purchase orders found"
                    onRowClick={(po) => navigate(`/purchase-order/${po._id}`)}
                    onDelete={(po) => {
                        setSelectedPurchaseOrder(po);
                        setConfirmModalOpen(true);
                    }}
                    disableDelete={(po) => po.status !== 'Ordered'}
                />

                {/* Confirm Cancel Modal */}
                {confirmModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-4">Confirm Cancel</h2>
                                <p className="mb-6">
                                    Are you sure you want to cancel the Ordered purchase order "{selectedPurchaseOrder?.poNumber}"? 
                                    This action cannot be undone.
                                </p>
                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={() => setConfirmModalOpen(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                    >
                                        Confirm Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default PurchaseOrderList;